import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { analytics, AnalyticsEvents } from "common/analytics";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import { ReseedSingleStep } from "features/bulkReseedEventInput/EnterReseedEvents";
import EnterFeedEvent from "features/feedPurchaseDetail/EnterFeedEvent";
import { FeedPurchaseSingleStep } from "features/feedPurchaseDetail/EnterFeedPurchaseEvent";
import { buildFeedPurchaseLineItem } from "features/feedPurchaseDetail/feedPurchasePayload";
import { checkConsistency } from "features/feedPurchaseDetail/feedPurchaseValidation";
import EnterFertilizerEvent from "features/fertilizerInput/EnterFertilizerEvent";
import {
  convertFromFormInput,
  FertilizerEventFormData,
} from "features/fertilizerInput/FertilizerEventFormData";
import EnterFieldPreparationEvent from "features/fieldPreparationInput/EnterFieldPreparationEvent";
import {
  convertFromFormInput as convertFromFieldPrepFormInput,
  FieldPreparationEventFormData,
} from "features/fieldPreparationInput/FieldPreparationEventFormData";
import { NameAndGuid } from "features/general/NameAndGuidMultiSelectDropdown";
import EnterYieldEvent from "features/yieldInput/EnterYieldEvent";
import {
  AMOUNT_INPUT_MODE,
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  GeneralFeedEvent,
  getReseedTypeFromMethod,
  GRASS_TYPE,
  RESEED_TOTAL_AMOUNT_UNIT,
  SEEDING_METHOD,
  useCreateFeedEvent,
  useCreateFeedPurchaseEvent,
  useCreateFertilizerEvent,
  useCreateFieldPreparationEvent,
  useCreateReseedEventForFarm,
  useCreateYieldEvent,
  YieldEvent,
} from "model";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "stores";
import type { EventFileUploadHandle } from "./EventFileUpload";
import EventFileUpload from "./EventFileUpload";
import EventFormDialog from "./EventFormDialog";

export function AddReseedEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const createReseedEventMutation = useCreateReseedEventForFarm();

  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const [newGrassType, setNewGrassType] = useState<GRASS_TYPE | undefined>(
    undefined
  );
  const [grassTypeError, setGrassTypeError] = useState<boolean>(false);
  const [seedingMethod, setSeedingMethod] = useState<
    SEEDING_METHOD | undefined
  >(undefined);
  const [seedingMethodError, setSeedingMethodError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [selectedFields, setSelectedFields] = useState<NameAndGuid[]>([]);
  const [reseedDate, setReseedDate] = useState<Date | null>(new Date());
  const [reseedDateError, setReseedDateError] = useState<boolean>(false);
  const [seedingRate, setSeedingRate] = useState<number>(
    "" as unknown as number
  );
  const [amountInputMode, setAmountInputMode] = useState<AMOUNT_INPUT_MODE>(
    AMOUNT_INPUT_MODE.RATE
  );
  const [totalAmount, setTotalAmount] = useState<number>(
    "" as unknown as number
  );
  const [totalAmountUnit, setTotalAmountUnit] =
    useState<RESEED_TOTAL_AMOUNT_UNIT>(RESEED_TOTAL_AMOUNT_UNIT.KG);
  const [productCost, setProductCost] = useState<number>(
    "" as unknown as number
  );
  const [productName, setProductName] = useState<string>("");
  const [fieldsSelectInputError, setFieldsSelectInputError] =
    useState<boolean>(false);
  const fileUploadRef = useRef<EventFileUploadHandle>(null);

  const hasChanges = () => {
    return (
      selectedFields.length > 0 ||
      newGrassType !== undefined ||
      seedingMethod !== undefined ||
      notes !== "" ||
      productName !== "" ||
      (productCost !== undefined &&
        productCost !== ("" as unknown as number)) ||
      (seedingRate !== undefined && seedingRate !== ("" as unknown as number))
    );
  };

  function validateRequired(): boolean {
    let valid = true;
    if (!newGrassType) {
      setGrassTypeError(true);
      valid = false;
    }
    if (!reseedDate) {
      setReseedDateError(true);
      valid = false;
    }
    if (selectedFields.length === 0) {
      setFieldsSelectInputError(true);
      valid = false;
    }
    if (!seedingMethod) {
      setSeedingMethodError(true);
      valid = false;
    }
    return valid;
  }

  async function handleSave() {
    if (!validateRequired()) return;

    const fieldGuids = selectedFields.map(
      (selectedField: NameAndGuid) => selectedField.guid ?? ""
    );

    const result = await createReseedEventMutation.mutateAsync({
      reseedEvent: {
        note: notes,
        reseedDate: reseedDate.toISOString(),
        newGrassType: newGrassType,
        seedingMethod: seedingMethod,
        fieldGuids,
        reseedType: getReseedTypeFromMethod(seedingMethod),
        productName: productName,
        seedingRate: seedingRate,
        totalAmount: (totalAmount as unknown) === "" ? undefined : totalAmount,
        totalAmountUnit: totalAmountUnit,
        amountInputMode: amountInputMode,
        productCost: (productCost as unknown) === "" ? undefined : productCost,
        productCostUnit: (productCost as unknown) === "" ? undefined : "GBP",
      },
      farmGuid: currentFarmId ?? "",
    });
    // Invalidate field queries for affected fields
    await Promise.all(
      fieldGuids.map((fieldGuid) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.fields.detail(fieldGuid),
        })
      )
    );
    // Upload any staged files
    if (result?.guid && fileUploadRef.current?.hasStagedFiles()) {
      await fileUploadRef.current.uploadStagedFiles(result.guid);
    }

    notify(t("updateGrassTypes.success"));
    analytics.track(AnalyticsEvents.RESEED_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <EventFormDialog
      open={dialogOpen}
      onClose={handleClose}
      title={t("reseedEvent.heading")}
      onSave={handleSave}
      isDirty={hasChanges}
      cancelAnalyticsEvent={AnalyticsEvents.RESEED_EVENT_CREATE_CANCELLED}
      steps={[
        {
          content: (
            <ReseedSingleStep
              reseedDate={reseedDate}
              setReseedDate={setReseedDate}
              reseedDateError={reseedDateError}
              setReseedDateError={setReseedDateError}
              selectedFields={selectedFields}
              setSelectedFields={setSelectedFields}
              fieldsSelectInputError={fieldsSelectInputError}
              setFieldsSelectInputError={setFieldsSelectInputError}
              enableFieldSelection={true}
              onSeedingMethodChange={(value) => {
                setSeedingMethod(value);
                if (value) {
                  setSeedingMethodError(false);
                } else {
                  setSeedingMethodError(true);
                }
              }}
              newGrassType={newGrassType}
              setNewGrassType={setNewGrassType}
              grassTypeError={grassTypeError}
              setGrassTypeError={setGrassTypeError}
              seedingMethod={seedingMethod}
              seedingMethodError={seedingMethodError}
              productName={productName}
              setProductName={setProductName}
              seedingRate={seedingRate}
              setSeedingRate={setSeedingRate}
              amountInputMode={amountInputMode}
              setAmountInputMode={setAmountInputMode}
              totalAmount={totalAmount}
              setTotalAmount={setTotalAmount}
              totalAmountUnit={totalAmountUnit}
              setTotalAmountUnit={setTotalAmountUnit}
              productCost={productCost}
              setProductCost={setProductCost}
              notes={notes}
              setNotes={setNotes}
              fileUploadSlot={<EventFileUpload ref={fileUploadRef} />}
            />
          ),
        },
      ]}
    />
  );
}

export function AddFeedEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createFeedEventMutation = useCreateFeedEvent();
  const dirtyRef = useRef(false);

  const handleClose = () => {
    setDialogOpen(false);
  };

  async function handleSave(generalFeedEvent: GeneralFeedEvent) {
    await createFeedEventMutation.mutateAsync({
      generalFeedEvent,
      farmGuid: currentFarmId ?? "",
    });
    notify(t("addFeedEvent.add.success"));
    analytics.track(AnalyticsEvents.FEED_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <EnterFeedEvent
      feedEvent={undefined}
      handleSave={handleSave}
      dirtyRef={dirtyRef}
    >
      {({ steps, submitForm }) => (
        <EventFormDialog
          open={dialogOpen}
          onClose={handleClose}
          title={t("feedEventInput.title")}
          onSave={submitForm}
          isDirty={() => dirtyRef.current}
          cancelAnalyticsEvent={AnalyticsEvents.FEED_EVENT_CREATE_CANCELLED}
          steps={steps}
        />
      )}
    </EnterFeedEvent>
  );
}

export function AddFeedPurchaseEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const createFeedPurchaseMutation = useCreateFeedPurchaseEvent();

  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const [feedType, setFeedType] = useState<GENERAL_FEED_TYPE | undefined>(
    undefined
  );
  const [feedTypeError, setFeedTypeError] = useState<boolean>(false);
  const [feedUnit, setFeedUnit] = useState<GENERAL_FEED_UNITS | undefined>(
    undefined
  );
  const [feedUnitError, setFeedUnitError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string | undefined>(undefined);
  const [cost, setCost] = useState("");
  const [costError, setCostError] = useState<boolean>(false);
  const [feedAmount, setFeedAmount] = useState("");
  const [feedAmountError, setFeedAmountError] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const [dateError, setDateError] = useState<boolean>(false);

  const hasChanges = () => {
    return (
      feedType !== undefined ||
      feedUnit !== undefined ||
      (feedAmount !== "" && feedAmount !== undefined) ||
      (cost !== "" && cost !== undefined) ||
      (notes !== undefined && notes !== "")
    );
  };

  async function createfeedPurchaseEvents() {
    if (!feedType || !date || !currentFarmId || !feedAmount) {
      return;
    }

    return await createFeedPurchaseMutation.mutateAsync({
      farmGuid: currentFarmId,
      guid: " ",
      note: notes ?? "",
      feedPurchaseEventDate: date.toISOString(),
      feedPurchases: [
        buildFeedPurchaseLineItem(
          feedType,
          feedUnit ?? GENERAL_FEED_UNITS.TONNE,
          feedAmount,
          cost === "" ? undefined : cost
        ),
      ],
    });
  }

  async function handleSave() {
    const consistent = checkConsistency(
      feedType,
      setFeedTypeError,
      feedUnit,
      setFeedUnitError,
      date,
      setDateError,
      dateError,
      feedAmount,
      setFeedAmountError,
      cost,
      setCostError
    );
    if (!consistent) {
      return;
    }
    await createfeedPurchaseEvents();

    notify(t("createfeedPurchaseEvent.success"));
    analytics.track(AnalyticsEvents.FEED_PURCHASE_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <EventFormDialog
      open={dialogOpen}
      onClose={handleClose}
      title={t("enterFeedPurchase.heading")}
      onSave={handleSave}
      isDirty={hasChanges}
      cancelAnalyticsEvent={AnalyticsEvents.FEED_PURCHASE_CREATE_CANCELLED}
      steps={[
        {
          content: (
            <FeedPurchaseSingleStep
              date={date}
              setDate={setDate}
              dateError={dateError}
              feedType={feedType}
              setFeedType={setFeedType}
              feedTypeError={feedTypeError}
              setFeedTypeError={setFeedTypeError}
              feedUnit={feedUnit}
              setFeedUnit={setFeedUnit}
              feedUnitError={feedUnitError}
              setFeedUnitError={setFeedUnitError}
              feedAmount={feedAmount}
              setFeedAmount={setFeedAmount}
              feedAmountError={feedAmountError}
              setFeedAmountError={setFeedAmountError}
              cost={cost}
              setCost={setCost}
              costError={costError}
              setCostError={setCostError}
              note={notes}
              setNotes={setNotes}
            />
          ),
        },
      ]}
    />
  );
}

export function AddYieldEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createYieldEventMutation = useCreateYieldEvent();
  const dirtyRef = useRef(false);

  const handleClose = () => {
    setDialogOpen(false);
  };

  async function handleSave(yieldEvent: YieldEvent) {
    await createYieldEventMutation.mutateAsync({
      yieldEvent,
      farmGuid: currentFarmId ?? "",
    });
    notify(t("addYieldEvent.add.success"));
    analytics.track(AnalyticsEvents.YIELD_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <EnterYieldEvent
      yieldEvent={undefined}
      handleSave={handleSave}
      dirtyRef={dirtyRef}
    >
      {({ steps, submitForm }) => (
        <EventFormDialog
          open={dialogOpen}
          onClose={handleClose}
          title={t("yieldEventInput.title")}
          onSave={submitForm}
          isDirty={() => dirtyRef.current}
          cancelAnalyticsEvent={AnalyticsEvents.YIELD_EVENT_CREATE_CANCELLED}
          steps={steps}
        />
      )}
    </EnterYieldEvent>
  );
}

export function AddFertilizerEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createFertilizerEventMutation = useCreateFertilizerEvent();
  const dirtyRef = useRef(false);
  const fertilizerFileUploadRef = useRef<EventFileUploadHandle>(null);

  const handleClose = () => {
    setDialogOpen(false);
  };

  async function handleSave(
    fertilizerEventFormData: FertilizerEventFormData,
    farmGuid: string | undefined
  ) {
    const fertilizerEventBody = convertFromFormInput(fertilizerEventFormData);
    farmGuid = farmGuid ?? "";
    const result = await createFertilizerEventMutation.mutateAsync({
      fertilizerEvent: fertilizerEventBody,
      farmGuid,
    });

    // Upload any staged files
    if (result?.guid && fertilizerFileUploadRef.current?.hasStagedFiles()) {
      await fertilizerFileUploadRef.current.uploadStagedFiles(result.guid);
    }

    notify(t("addFertilizerEvent.add.success"));
    analytics.track(AnalyticsEvents.FERTILIZER_EVENT_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <EnterFertilizerEvent
      fertilizerEvent={undefined}
      handleSave={handleSave}
      fieldSelectDisabled={false}
      dirtyRef={dirtyRef}
      fileUploadSlot={<EventFileUpload ref={fertilizerFileUploadRef} />}
    >
      {({ steps, submitForm }) => (
        <EventFormDialog
          open={dialogOpen}
          onClose={handleClose}
          title={t("fertilizerEventInput.title")}
          onSave={submitForm}
          isDirty={() => dirtyRef.current}
          cancelAnalyticsEvent={
            AnalyticsEvents.FERTILIZER_EVENT_CREATE_CANCELLED
          }
          steps={steps}
        />
      )}
    </EnterFertilizerEvent>
  );
}

export function AddFieldPreparationEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const createFieldPreparationEventMutation = useCreateFieldPreparationEvent();
  const dirtyRef = useRef(false);
  const fieldPrepFileUploadRef = useRef<EventFileUploadHandle>(null);

  const handleClose = () => {
    setDialogOpen(false);
  };

  async function handleSave(
    fieldPreparationEventFormData: FieldPreparationEventFormData
  ) {
    const fieldPreparationEvent = convertFromFieldPrepFormInput(
      fieldPreparationEventFormData
    );
    const result = await createFieldPreparationEventMutation.mutateAsync({
      fieldPreparationEvent,
      farmGuid: currentFarmId ?? "",
    });

    // Upload any staged files
    if (result?.guid && fieldPrepFileUploadRef.current?.hasStagedFiles()) {
      await fieldPrepFileUploadRef.current.uploadStagedFiles(result.guid);
    }

    notify(t("addFieldPreparationEvent.add.success"));
    analytics.track(AnalyticsEvents.FIELD_PREPARATION_CREATED);
    setDialogOpen(false);
    navigate("/records");
  }

  return (
    <EnterFieldPreparationEvent
      fieldPreparationEvent={undefined}
      handleSave={handleSave}
      fieldSelectDisabled={false}
      dirtyRef={dirtyRef}
      fileUploadSlot={<EventFileUpload ref={fieldPrepFileUploadRef} />}
    >
      {({ steps, submitForm }) => (
        <EventFormDialog
          open={dialogOpen}
          onClose={handleClose}
          title={t("fieldPreparationEventInput.title")}
          onSave={submitForm}
          isDirty={() => dirtyRef.current}
          cancelAnalyticsEvent={
            AnalyticsEvents.FIELD_PREPARATION_CREATE_CANCELLED
          }
          steps={steps}
        />
      )}
    </EnterFieldPreparationEvent>
  );
}
